<template>
  <div class="min-h-screen">
    <!-- SEO Meta Tags -->
    <PageSEO
      title="Frontend Development Services - Modern UI/UX Solutions"
      description="Modern, responsive user interfaces built with cutting-edge frontend technologies. We create engaging, fast, and accessible web experiences using Vue, React, and more."
      :keywords="['frontend development', 'UI/UX design', 'responsive design', 'Vue.js', 'React', 'JavaScript', 'TypeScript', 'modern web interfaces']"
      url="https://vedware.com/services/frontend-development"
    />

    <!-- Hero Section -->
    <HeroSection
      :icon="PhMonitor"
      title="Frontend"
      highlighted-title="Development"
      description="Modern, responsive user interfaces built with cutting-edge frontend technologies. We create engaging, fast, and accessible web experiences that users love."
    />

    <!-- Service Overview -->
    <ServiceOverview
      :description="[
        'Our frontend development service focuses on creating exceptional user experiences through modern, responsive interfaces. We specialize in building fast, accessible, and visually appealing web applications.',
        'From single-page applications to complex dashboards, we leverage the latest frontend frameworks and tools to deliver solutions that are both beautiful and functional across all devices and browsers.',
      ]"
      :features="[
        'Responsive design for all screen sizes',
        'Modern JavaScript frameworks and libraries',
        'Optimized performance and fast loading times',
        'Accessibility compliance (WCAG guidelines)',
        'Cross-browser compatibility',
        'Progressive Web App (PWA) capabilities',
      ]"
    />

    <!-- Technologies Section -->
    <ServiceTechnologies
      title="Frontend Technologies"
      description="We use the latest frontend technologies and frameworks to build modern, performant user interfaces."
      :categories="technologyCategories"
    />

    <!-- Services Offered -->
    <ServiceOfferings
      title="Frontend Services"
      description="We offer a comprehensive range of frontend development services to meet your specific needs."
      :services="frontendServices"
    />

    <ServiceProjects slug="frontend-development" />

    <!-- CTA Section -->
    <ServiceCTA
      title="Ready to Create an Amazing"
      highlighted-title="User Experience?"
      description="Let's build a frontend that engages your users and drives your business forward."
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from '#imports'
import {
  PhChartLineUp,
  PhDeviceMobile,
  PhEye,
  PhGlobe,
  PhLightning,
  PhMonitor,
  PhPalette,
  PhStack,
  PhWrench,
} from '@phosphor-icons/vue'
import ServiceCTA from '~/components/Services/ServiceCTA.vue'
import ServiceOfferings from '~/components/Services/ServiceOfferings.vue'
import ServiceOverview from '~/components/Services/ServiceOverview.vue'
import ServiceProjects from '~/components/Services/ServiceProjects.vue'
import ServiceTechnologies from '~/components/Services/ServiceTechnologies.vue'
import HeroSection from '~/components/Shared/HeroSection.vue'
import PageSEO from '~/components/Shared/PageSEO.vue'

const technologyCategories = ref([
  {
    title: 'Frameworks',
    icon: PhStack,
    technologies: [
      'Vue',
      'Nuxt.js',
      'React',
      'Svelte',
      'JavaScript',
      'TypeScript',
      'Vuex',
      'Pinia',
      'Redux',
      'Redux Thunk',
    ],
  },
  {
    title: 'Styling & UI',
    icon: PhPalette,
    technologies: [
      'HTML5',
      'CSS3',
      'Tailwind CSS',
      'Bootstrap',
      'Sass',
      'jQuery',
      'PSD to HTML',
      'Pixel Perfect',
      'Responsive Design',
    ],
  },
  {
    title: 'Tools & Performance',
    icon: PhWrench,
    technologies: [
      'Webpack',
      'Vite',
      'NPM',
      'Ajax',
      'WebSockets',
      'Socket.IO',
      'PWA',
      'Performance Optimization',
    ],
  },
])

const frontendServices = ref([
  {
    icon: PhDeviceMobile,
    title: 'Responsive Web Design',
    description: 'Mobile-first design approach ensuring your website looks perfect on all devices and screen sizes.',
  },
  {
    icon: PhLightning,
    title: 'Single Page Applications',
    description: 'Fast, interactive SPAs built with modern frameworks for seamless user experiences.',
  },
  {
    icon: PhStack,
    title: 'Component Development',
    description: 'Reusable, maintainable UI components that can be easily integrated and customized.',
  },
  {
    icon: PhChartLineUp,
    title: 'Performance Optimization',
    description: 'Code splitting, lazy loading, and optimization techniques for lightning-fast load times.',
  },
  {
    icon: PhEye,
    title: 'UI/UX Implementation',
    description: 'Converting design mockups into pixel-perfect, interactive user interfaces.',
  },
  {
    icon: PhGlobe,
    title: 'Progressive Web Apps',
    description: 'PWAs that work offline, load instantly, and provide native app-like experiences.',
  },
])
</script>
